import { IResourceToken } from './DataServiceInterfaces.ts';
import { ChangeFeedOptions, Container, CosmosClient, Database, StatusCodes, TokenProvider } from '@azure/cosmos';
import { v4 as uuid } from 'uuid';
import { GetAuthServices, TokenScope } from '../AuthService/AuthService.tsx';
import { EntityWatcher } from './EntityWatcher.ts';
import { BaseProperties, DataEntityBase } from './Model/Base/DataEntityBase.ts';
import { IDataModel } from './Model/Base/Models.ts';
import { Config } from '../config.ts';
import { PersistentDataCache } from './PersistentDataCache.ts';
import { ICacheableItem } from './BaseCache.ts';
import { appInsights } from '../AppInsightsService.tsx';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import demoData from './demoData.json?raw';
import { EventHookManager } from '../../Utilities/EventHookManager.ts';
import { Mutex } from '../../Utilities/Mutext.ts';


interface ICosmosClientInfo {
    client: CosmosClient;
    collection: Container;
    database: Database;
    partitionKeyHeader: string;
    resourceToken: IResourceToken;
    data?: IDataModel;
    isTestAccount: boolean;
}


const _ContinuationTokenKey = "DataService | ContinuationToken";

const getContinuationToken = (scope: TokenScope) => `${_ContinuationTokenKey} | ${scope}`;
const DataCacheKey = "DataService | Cache";
const doLogging = false;

var entityWatcher: EntityWatcher<DataEntityBase> | null = null;
var clientInfoMap: Map<TokenScope, ICosmosClientInfo> = new Map();
var clientLogInfo: any = {};

export interface ICacheUpdateArg {
    items: ICacheableItem[];
}
var cachedItemChanged = new EventHookManager<ICacheUpdateArg>();


/**
 * Custom hook for data service.
 * Provides functions for reading and writing data to Cosmos DB.
 */
function useDataService() {

    GetAuthServices().then(async (authServices) => {


        authServices.userDeleted.Subscribe(async () => {

            appInsights.trackEvent({ name: "AuthServices | userDeleted", });
            appInsights.trackTrace({ message: "Deleting all data", severityLevel: SeverityLevel.Warning });
            var batchSize = 10;
            var batch: Promise<any>[] = [];
            for (let key of typeMap.keys()) {
                batch.push(dataCache.DeleteData(key));
                if (batch.length >= batchSize) {
                    await Promise.all(batch);
                    batch = [];
                }
            }
            appInsights.trackTrace({ message: "All data deleted", severityLevel: SeverityLevel.Information });
            let auth = await GetAuthServices();

            appInsights.trackTrace({ message: "Logging out user", severityLevel: SeverityLevel.Information });
            await auth.LogOutUser();
        });
    });


    /**
     * Retrieves the partition key from the given ICosmosClientInfo object.
     * @param info - The ICosmosClientInfo object.
     * @returns The partition key.
     */
    const GetPartitionKey = (info: ICosmosClientInfo) => {
        return info.resourceToken.resourcePermissions[0].partitionKey
    };




    /**
     * Retrieves the Cosmos DB client.
     * @returns A Promise that resolves to the ICosmosClientInfo object.
     */
    const GetCosmosClient = async (scope: TokenScope = TokenScope.UserReadWrite): Promise<ICosmosClientInfo> => {

        appInsights.startTrackEvent('DataServices | GetCosmosClient');


        // Used to get the resourceToken.
        //
        const authServices = await GetAuthServices();
        var resourceToken = await authServices.AcquireResourceToken(scope) as IResourceToken;

        doLogging && console.log('ResourceToken | ', resourceToken);
        appInsights.trackTrace({ message: "GetCosmosClient | ResourceToken", severityLevel: SeverityLevel.Verbose, properties: { scope: scope, resourceToken: resourceToken } });
        if (resourceToken) {

            //
            // Get readwrite permissions from the resource token.
            //
            var permission = resourceToken.resourcePermissions.find((p) => p.scope === scope);
            if (!permission) {
                appInsights.trackException({ exception: new Error('Unable to find a valid permission for scope: ' + scope) });
                throw new Error('Unable to find a valid permission for scope: ' + scope);
            }
            const previousPermision = clientInfoMap.get(scope)?.resourceToken.resourcePermissions[0];
            //
            // Create a new client if the resource token has changed.
            //
            if (!clientInfoMap.get(scope)
                || previousPermision !== permission
            ) {
                appInsights.trackTrace({ message: "Creating new CosmosClient", severityLevel: SeverityLevel.Verbose, properties: { scope: scope, permission: permission } });

                const tokenProvider: TokenProvider = async (_request) => {
                    doLogging && console.log(`CosmosClient | TokenProvider(${scope}) | `, permission!.resourceToken, _request);
                    return permission!.resourceToken!
                }
                var client = new CosmosClient({
                    endpoint: resourceToken.endpointUrl,
                    tokenProvider: tokenProvider
                });

                var databaseId = Config.DataBase ?? resourceToken.databaseId;
                var collectionId = Config.Collection ?? resourceToken.collectionId;

                var database = client.database(databaseId);
                var collection = database.container(collectionId);

                clientLogInfo = {
                    databaseId: databaseId,
                    collectionId: collectionId,
                }
                clientInfoMap.set(
                    scope,
                    {
                        client: client,
                        collection: collection,
                        database: database,
                        partitionKeyHeader: resourceToken.partitionKeyHeader,
                        resourceToken: resourceToken,
                        isTestAccount: authServices.IsTestAccount()
                    }
                );
            }

            //
            // Get the database and collection based on the resource token.
            //
            appInsights.trackTrace({ message: "CosmosClient | TargetInfo", severityLevel: SeverityLevel.Verbose, properties: { scope: scope, clientInfo: clientInfoMap.get(scope) } });
            doLogging && console.log('CosmosClient | TargetInfo | ', clientLogInfo);

            appInsights.stopTrackEvent('DataServices | GetCosmosClient');
            return clientInfoMap.get(scope)!;
        }
        throw new Error('Invalid resource token');
    }


    var isCacheInitialized = false;
    const Initialize = async (shouldAwait = false) => {

        //
        // Remove the old cache if it exists.
        //
        const oldCacheString = 'dataServiceCache';
        if (window.localStorage.getItem(oldCacheString)) {
            appInsights.trackEvent({ name: "DataService | Removing old cache" });
            window.localStorage.removeItem(oldCacheString);
        }

        const oldContiunationToken = "continuationToken";
        if (window.localStorage.getItem(oldContiunationToken)) {
            appInsights.trackEvent({ name: "DataService | Removing old continuation token" });
            window.localStorage.removeItem(oldContiunationToken);
        }


        let promises: Promise<any>[] = [];
        if (!isCacheInitialized) {
            promises.push(dataCache.InitializeCache(shouldAwait));
            dataCache.ForEach((value) => {
                let obj = JSON.parse(value);
                typeMap.set(obj[BaseProperties.id], obj[BaseProperties.typename]);
            })
            promises.push(readonlyCache.InitializeCache(shouldAwait));
        }

        if (shouldAwait)
            await Promise.all(promises);

        isCacheInitialized = true;
    }



    const typeMap = new Map<string, string>();
    const changeFeedMutext = new Mutex("DataService.changeFeedGuard");
    const processChangeFeed = async (scope: TokenScope = TokenScope.UserReadWrite) => {

        let isNewCache = IsNewCache();
        //
        // Intentionally not awaiting the locking here to make sure that we handle this asynchonously.
        //
        let processingPromise = changeFeedMutext.lockSingle(
            async () => {

                appInsights.startTrackEvent('DataServices | processChangeFeed');

                appInsights.trackTrace({ message: "Processing change feed", severityLevel: SeverityLevel.Information, properties: { scope: scope } });
                console.log(`${new Date().toISOString()} > Processing change feed`);

                var clientInfo = await GetCosmosClient(scope);

                //
                // We don't need to update the cache - there is nothing on the server.
                //
                if (clientInfo.isTestAccount) {

                    dataCache.ClearCache();
                    dataCache.FlushCache(demoData);
                    dataCache.RestoreCache();

                    dataCache.ForEach((value) => {
                        let obj = JSON.parse(value);
                        typeMap.set(obj[BaseProperties.id], obj[BaseProperties.typename]);
                    })

                    appInsights.stopTrackEvent('DataServices | processChangeFeed');
                    return;
                }

                //
                // Need to persist and retrieve the continuation token, this starts from the beginning and is 
                // useful for a users first login.
                //
                let continuationStr = window.localStorage.getItem(getContinuationToken(scope)) ?? undefined;
                const options: ChangeFeedOptions = {
                    continuation: continuationStr,
                    startFromBeginning: !continuationStr
                };
                const iterator = clientInfo.collection.items.changeFeed(GetPartitionKey(clientInfo), options);

                appInsights.trackTrace({ message: "ChangeFeed | Start", severityLevel: SeverityLevel.Information, properties: { scope: scope, continuationStr: continuationStr } });
                var changedItems: ICacheableItem[] = [];
                while (iterator.hasMoreResults) {

                    appInsights.trackTrace({ message: "ChangeFeed | Fetching next batch" });
                    console.log(`${new Date().toISOString()} > Fetching next batch`);
                    const response = await iterator.fetchNext();
                    if (response.statusCode === StatusCodes.NotModified) {
                        continuationStr = response.continuation;
                        //
                        // store the continuation token in local storage
                        //
                        window.localStorage.setItem(getContinuationToken(scope), continuationStr);

                        appInsights.trackTrace({ message: "ChangeFeed | No more server changes", severityLevel: SeverityLevel.Information, properties: { continuationStr: continuationStr } });
                        console.log(`${new Date().toISOString()} > No more server changes... creating typeMap  continuation token: `, continuationStr);
                        dataCache.ForEach((value) => {
                            let obj = JSON.parse(value);
                            typeMap.set(obj[BaseProperties.id], obj[BaseProperties.typename]);
                        })

                        appInsights.trackTrace({ message: "ChangeFeed | Processing complete", severityLevel: SeverityLevel.Information });
                        console.log(`${new Date().toISOString()} > Processing complete`);

                        if (changedItems.length > 0) {
                            console.log('ChangeFeed | CachedItemChanged | ', changedItems.length);
                            cachedItemChanged.Fire({ items: changedItems });
                        }

                        return;
                    }
                    else {
                        appInsights.trackTrace({ message: "ChangeFeed | Processing change feed response", severityLevel: SeverityLevel.Information, properties: { response: response.result.length } });
                        console.log(`${new Date().toISOString()} > Processing change feed response: ` + response.result.length);
                        var items = response.result as any as DataEntityBase[];
                        let modifiedItems = await dataCache.UnsafeSetDataArray(items);
                        changedItems.push(...modifiedItems);
                    }
                }

                appInsights.stopTrackEvent('DataServices | processChangeFeed');
            }
        );

        if (isNewCache && processingPromise)
            await processingPromise;
    }




    /**
     * Writes data to a Cosmos DB collection. This function ensures that the data has a PartitionKey and id,
     * generating them if necessary, before upserting the data into the Cosmos DB collection.
     *
     * @param data - The data object to be written to the Cosmos DB collection. It can be of any type.
     * @returns A promise that resolves to the result of the upsert operation, or undefined if an error occurs.
     */
    const writeDataInCloud = async (data: ICacheableItem) => {
        try {
            appInsights.startTrackEvent('DataServices | writeDataInCloud');

            //
            // Ensure that the data has a PartitionKey and id.
            //
            if (!(data instanceof DataEntityBase)) {
                appInsights.trackException({ exception: new Error('Data must be an instance of DataEntityBase') });
                throw new Error('Data must be an instance of DataEntityBase');
            }

            appInsights.trackTrace({ message: "DataSerivces | Writing", severityLevel: SeverityLevel.Verbose, properties: { id: data.id } });
            doLogging && console.log('DataSerivces | Writing | ', data.id);

            var clientInfo = await GetCosmosClient();


            var typedData = data as DataEntityBase;
            typedData._PartitionKey ??= GetPartitionKey(clientInfo);
            typedData.key ??= uuid();

            // Prepare the data for storage
            //
            var sdata = typedData.Serialize();
            typeMap.set(sdata[BaseProperties.id], sdata[BaseProperties.typename]);


            if (clientInfo.isTestAccount) {
                appInsights.trackTrace({ message: "DataSerivces | WriteDataInCloud | TestAccount", severityLevel: SeverityLevel.Warning });
                return;
            }

            //
            // Write the data to the Cosmos DB collection.
            //
            await clientInfo.collection.items.upsert(sdata);

        } catch (error) {
            appInsights.trackException({ exception: error as Error });
            console.error(error);
        } finally {
            appInsights.stopTrackEvent('DataServices | writeDataInCloud');
        }
    }

    /**
     * Reads data from a Cosmos DB collection using the specified ID.
     * This function retrieves the Cosmos client information, then attempts
     * to read the item with the given ID from the collection.
     *
     * @param id - The ID of the item to read from the Cosmos DB collection.
     * @returns A promise that resolves to the result of the read operation,
     *          or null if an error occurs.
     */
    const readDataInCloud = async (_id: string, _scope: TokenScope = TokenScope.UserReadWrite) => {

        //
        // Moved to a cache first implementation.  We won't actually read from the cloud, but we will
        // trigger the change feed to update the cache.
        //
        (doLogging) && console.log(`Reading '${_id}'`);
        processChangeFeed(_scope);
        return null;
    }

    const deleteDataInCloud = async (id: string) => {
        try {
            appInsights.startTrackEvent('DataServices | deleteDataInCloud');
            var clientInfo = await GetCosmosClient();

            var clientInfo = await GetCosmosClient();
            if (clientInfo.isTestAccount) {
                appInsights.trackTrace({ message: "DataSerivces | DeleteDataInCloud | TestAccount", severityLevel: SeverityLevel.Warning });
                return;
            }

            var result = await clientInfo.collection.item(id, GetPartitionKey(clientInfo)).delete();

            typeMap.delete(id);
            appInsights.trackTrace({ message: "DataSerivces | Deleted", severityLevel: SeverityLevel.Verbose, properties: { id: id, result: result } });
            doLogging && console.log(`Deleted data: `, result);
            return;
        } catch (error) {
            return;
        } finally {
            appInsights.stopTrackEvent('DataServices | deleteDataInCloud');
        }

    }

    const readAllByType = async (type: string) => {

        try {
            appInsights.startTrackEvent('DataServices | readAllByType');

            await dataCache.InitializeCache();
            let retVal: any[] = [];
            for (let [key, value] of typeMap) {
                const id = key;
                if (value === type) {
                    retVal.push(
                        await dataCache.ReadData(id)
                    );
                }
            };
            return retVal;
        }
        finally {
            appInsights.stopTrackEvent('DataServices | readAllByType');
        }
    }

    const allIdsByType = (type: string) => {
        try {
            appInsights.startTrackEvent('DataServices | allIdsByType');
            let retVal: string[] = [];
            for (let [key, value] of typeMap) {
                const id = key;
                if (value === type) {
                    retVal.push(id);
                }
            };
            return retVal;
        }
        finally {
            appInsights.stopTrackEvent('DataServices | allIdsByType');
        }
    }

    const ClearCaches = () => {
        dataCache.ClearCache();
        readonlyCache.ClearCache();
        window.localStorage.removeItem(getContinuationToken(TokenScope.UserReadWrite));
        window.localStorage.removeItem(getContinuationToken(TokenScope.SharedReadOnly));
        isCacheInitialized = false;
    }

    const IsNewCache = () => {

        let item1 = window.localStorage.getItem(getContinuationToken(TokenScope.UserReadWrite));
        let item2 = window.localStorage.getItem(getContinuationToken(TokenScope.SharedReadOnly));
        return item1 === null || item2 === null;
    }


    const dataCache = new PersistentDataCache(
        DataCacheKey,
        {
            doReadFN: readDataInCloud,
            doWriteFN: writeDataInCloud,
            doDeleteFN: deleteDataInCloud,
            debugName: "DataService",
            skipCache: false,
            syncExternalChanges: async () => { await processChangeFeed(TokenScope.UserReadWrite) },
            updateVersionOnWrite: true
        }
    );

    const readonlyCache = new PersistentDataCache(
        'readonlyCache',
        {
            doReadFN: (id: string) => readDataInCloud(id, TokenScope.SharedReadOnly),
            doWriteFN: (): Promise<any> => { throw new Error('Cannot write to readonly cache') },
            doDeleteFN: (): Promise<any> => { throw new Error('Cannot delete from readonly cache') },
            debugName: "DataService",
            skipCache: false,
            syncExternalChanges: async () => { await processChangeFeed(TokenScope.SharedReadOnly) }
        }
    );


    entityWatcher ??= new EntityWatcher<DataEntityBase>(
        async (entity) => { await dataCache.WriteData(entity) }
    );


    return {
        Initialize,
        IsNewCache,
        Raw: {
            writeData: dataCache.WriteData,
            readData: dataCache.ReadData,
            readAllByType: readAllByType,
            allIdsByType: allIdsByType,
            deleteData: dataCache.DeleteData,
            cachedItemChanged: cachedItemChanged.eventHook,
            ClearCaches
        },
        ReadOnly: {
            readData: readonlyCache.ReadData,
        },
        EntityWatcher: entityWatcher
    };
}

export const DataServices = useDataService();